import React, { useEffect, useState } from 'react';

import { CloseOutlined } from '@kufox/icons';
import { Box, Drawer, Divider, ThemeProvider } from '@kufox/mui';
import { useResponsive, useTheme } from '@kufox/mui/hooks';
import { styled, isPropValid } from '@kufox/mui/emotion';

import { Tabs, HumanCaptcha } from '../components';
import { useSelector, useDispatch } from 'react-redux';
import _ from 'lodash';
import { ga, kcsensorsManualTrack, getSensorsABResult, getDefaultSign } from '../common/tools';
import remoteEvent from '@tools/remoteEvent';
import uniqueModel from '@tools/uniqueModel';
import { extendModel } from '@utils';
import useConstructor from '@hooks/useConstructor';

import Layout from '../Layout';
import EmailSign from './EmailSign';
import PhoneSign from './PhoneSign';
import NewEmailSign from './NewEmailSign';
import NewPhoneSign from './NewPhoneSign';
import AccountActive from './AccountActive';
import {
  SIGN_EMAIL_TAB_KEY,
  EMAIL_BIZTYPE,
  PHONE_BIZTYPE,
  SIGN_PHONE_TAB_KEY,
  NAMESPACE,
  REGISTER_STEP_ONE,
  REGISTER_STEP_TWO,
  CONTROL_AB_GROUP,
  EXPERIMENT_AB_GROUP,
  SIGNUP_AB_CONFIG,
} from './constants';

import { useLang } from '../hookTool';
import signUpModel from './model';

const Title = styled('h3', {
  shouldForwardProp: (props) => isPropValid(props),
})(({ isKuMex }) => {
  if (isKuMex) {
    return {
      fontWeight: '500',
      fontSize: '24px',
      lineHeight: '36px',
    };
  }
  return {
    fontSize: '34px',
    lineHeight: '48px',
    marginBottom: '40px',
  };
});

const CloseBox = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(() => {
  return {
    position: 'absolute',
    top: 24,
    right: 24,
  };
});

const CloseIcon = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    width: '48px',
    height: '48px',
    backgroundColor: theme.colors.icon,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '100%',
    cursor: 'pointer',
  };
});

const { withLayout } = Layout;

const tabKeysMap = {
  [SIGN_EMAIL_TAB_KEY]: {
    bizType: EMAIL_BIZTYPE,
  },
  [SIGN_PHONE_TAB_KEY]: {
    bizType: PHONE_BIZTYPE,
  },
};

function SignUp(props = {}) {
  useConstructor(() => {
    remoteEvent.emit(remoteEvent.evts.GET_DVA, (dva, m) => {
      uniqueModel(dva, extendModel(m, signUpModel));
    });
  });
  const {
    onChange,
    agreeJSX,
    BoxProps = {},
    tabKey,
    initEmail = '',
    initPhone = '',
    bonusImg,
    recallType = null,
    fromDrawer = false,
    forgetLeft,
    useAB = false,
    showDiscount = false,
  } = props;

  const defaultSign = getDefaultSign();
  const defaultTabKey = tabKey || SIGN_PHONE_TAB_KEY;
  const [_tabKey, setTabKey] = useState(defaultTabKey);
  const { t } = useLang();
  const dispatch = useDispatch();
  const { bizType } = tabKeysMap[_tabKey];
  const rv = useResponsive();
  const [step, setStep] = useState(REGISTER_STEP_ONE);
  const [renderTabs, setRenderTabs] = useState(CONTROL_AB_GROUP);
  const [isABLoading, setIsABLoading] = useState(true);
  const isNew = useAB && renderTabs === EXPERIMENT_AB_GROUP;
  // const isNew = true;
  const isSSG = navigator.userAgent.indexOf('SSG_ENV') > -1;

  const handleTabChange = (tabKey) => {
    setTabKey(tabKey);
    let gaKey = '';
    if (tabKey === SIGN_PHONE_TAB_KEY) {
      gaKey = '1';
    } else if (tabKey === SIGN_EMAIL_TAB_KEY) {
      gaKey = '2';
    }
    // 切换按钮
    ga('eleClickCollectionV1', {
      str1: 'KucoinWeb',
      str2: 'KucoinWeb.register',
      str3: 'KucoinWeb.register.SwitchButton',
      str4: `KucoinWeb.register.SwitchButton.${gaKey}`,
    });
  };

  useEffect(() => {
    setTabKey(defaultTabKey);
    return () => {};
  }, [tabKey]);

  useEffect(() => {
    pageVisitGa();
    eleExposureGa();
    return () => {
      dispatch({ type: `${NAMESPACE}/resetInit` });
    };
  }, []);

  // 页面访问事件集合
  const pageVisitGa = () => {
    ga('pageVisitCollectionV1', {
      siteid: 'KucoinWeb',
      siteid_pagecate: 'KucoinWeb_K0001',
      siteid_pagecate_pageid: 'KucoinWeb_K0001_Register',
    });
  };
  // 坑位曝光事件集合
  const eleExposureGa = () => {
    // 手机注册按钮
    ga('eleExposureCollectionV1', {
      str1: 'KucoinWeb',
      str2: 'KucoinWeb.register',
      str3: 'KucoinWeb.register.SwitchButton',
      str4: 'KucoinWeb.register.SwitchButton.1',
    });
    // 邮箱注册按钮
    ga('eleExposureCollectionV1', {
      str1: 'KucoinWeb',
      str2: 'KucoinWeb.register',
      str3: 'KucoinWeb.register.SwitchButton',
      str4: 'KucoinWeb.register.SwitchButton.2',
    });
  };
  const oldtabs = {
    [SIGN_PHONE_TAB_KEY]: {
      title: t(SIGN_PHONE_TAB_KEY),
      render: (props = {}) => {
        return <PhoneSign {...props} fromDrawer={fromDrawer} />;
      },
    },
    [SIGN_EMAIL_TAB_KEY]: {
      title: t(SIGN_EMAIL_TAB_KEY),
      render: (props = {}) => {
        return <EmailSign {...props} fromDrawer={fromDrawer} />;
      },
    },
  };

  const newtabs = {
    [SIGN_EMAIL_TAB_KEY]: {
      title: t(SIGN_EMAIL_TAB_KEY),
      render: (props = {}) => {
        return <NewEmailSign {...props} fromDrawer={fromDrawer} />;
      },
    },
    [SIGN_PHONE_TAB_KEY]: {
      title: t(SIGN_PHONE_TAB_KEY),
      render: (props = {}) => {
        return <NewPhoneSign {...props} fromDrawer={fromDrawer} />;
      },
    },
  };

  const tabs = isNew ? newtabs : oldtabs;
  // const tabs = true ? newtabs : oldtabs;

  const handleOneFinish = () => {
    setStep(REGISTER_STEP_TWO);
  };

  useEffect(() => {
    const fetchDatas = async () => {
      try {
        const _data1 = await dispatch({ type: `${NAMESPACE}/getSensorsABDefault`, payload: {} });
        const _config = {
          ...SIGNUP_AB_CONFIG,
          default_value: _data1,
        };
        const _data2 = await getSensorsABResult(_config);
        setRenderTabs(_data2);
      } catch (err) {
        console.log(err, '--err---');
      } finally {
        setIsABLoading(false);
      }
    };
    if (useAB) {
      fetchDatas();
    } else {
      setIsABLoading(false);
    }
  }, []);

  useEffect(() => {
    dispatch({
      type: `${NAMESPACE}/update`,
      payload: { isNew },
    });
    if (isNew) setTabKey(defaultSign);
  }, [isNew, defaultSign]);

  if (isSSG || isABLoading) return null;

  return (
    <>
      <HumanCaptcha bizType={bizType} namespace={NAMESPACE} nowPage="signUp" isNew={isNew} />
      {step === REGISTER_STEP_TWO ? (
        <AccountActive {...props} />
      ) : (
        <Box style={{ flex: !rv.md ? 1 : 'auto', maxWidth: rv.md ? 480 : '100%' }} {...BoxProps}>
          <Title>{t('sign')}</Title>
          <Tabs defaultKey={_tabKey} onChange={handleTabChange} isRender={false}>
            {_.map(tabs, ({ title }, key) => {
              return <Tabs.TabPane key={key} title={title} />;
            })}
          </Tabs>
          <>
            {tabs[_tabKey].render({
              onChange,
              agreeJSX,
              initEmail,
              initPhone,
              bonusImg,
              recallType,
              forgetLeft,
              showDiscount,
              onFinish: handleOneFinish,
            })}
          </>
        </Box>
      )}
    </>
  );
}

const SignUpWithTheme = ({ theme, ...others }) => {
  return (
    <ThemeProvider theme={theme || 'light'}>
      <SignUp {...others} />
    </ThemeProvider>
  );
};

const TabBox = styled('div', {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    fontSize: '14px',
    lineHeight: '22px',
    color: theme.colors.primary,
    cursor: 'pointer',
  };
});

const Tips = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(() => {
  return {
    display: 'flex',
    height: 22,
    fontSize: '14px',
    lineHeight: '22px',
  };
});

const TopBox = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(() => {
  return {
    marginBottom: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };
});

const Fab = styled(Box, {
  shouldForwardProp: (props) => isPropValid(props),
})(({ theme }) => {
  return {
    position: 'absolute',
    top: 16,
    right: 16,
    width: 24,
    height: 24,
    backgroundColor: theme.color.cover8,
  };
});

export const Signup4KuMEXDrawerWithTheme = (props) => {
  useConstructor(() => {
    remoteEvent.emit(remoteEvent.evts.GET_DVA, (dva, m) => {
      uniqueModel(dva, extendModel(m, signUpModel));
    });
  });

  const {
    open,
    BoxProps,
    agreeJSX = null,
    onChange = _.noop,
    handleLogin = _.noop,
    logo,
    NumberComponent,
    Footer = null,
    ZendeskIcon = null,
    defaultCountryCode = 'CN',
    trackingConfig = {},
    ...restProps
  } = props;

  const { t } = useLang();

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({ type: `${NAMESPACE}/getCountryCodes` });
  }, []);

  // 侧边栏注册-曝光埋点
  useEffect(() => {
    if (open) {
      kcsensorsManualTrack({
        spm: ['popUp', '1'],
        data: {
          pagecate: 'sideRegister',
          ...((trackingConfig && trackingConfig.data) || {}),
        },
      });
    }
  }, [open]);

  const { bizType, countryCodes } = useSelector((state) => state[NAMESPACE]);

  const handleTypeChange = (tabKey) => {
    const convertMaps = {
      [PHONE_BIZTYPE]: EMAIL_BIZTYPE,
      [EMAIL_BIZTYPE]: PHONE_BIZTYPE,
    };
    dispatch({
      type: `${NAMESPACE}/update`,
      payload: {
        bizType: convertMaps[tabKey],
      },
    });
  };

  useEffect(() => {
    if (!_.isEmpty(countryCodes)) {
      const countryCode = _.find(countryCodes, (o) => {
        return o.code === defaultCountryCode;
      });

      if (!countryCode) {
        dispatch({
          type: `${NAMESPACE}/update`,
          payload: {
            bizType: EMAIL_BIZTYPE,
          },
        });
      } else {
        dispatch({
          type: `${NAMESPACE}/update`,
          payload: {
            bizType: PHONE_BIZTYPE,
          },
        });
      }
    }
  }, [countryCodes, defaultCountryCode]);

  const theme = useTheme();

  return (
    <Drawer open={open} {...restProps}>
      <>
        <Box height="48px">{logo}</Box>
        <Fab onClick={restProps.onClose}>
          <CloseOutlined size={24} />
        </Fab>

        <Box maxWidth={480} mt={3.5} p={2} {...BoxProps}>
          <TopBox>
            <Title isKuMex as="div">
              {t('signup_tip')}
            </Title>
            <TabBox theme={theme} onClick={() => handleTypeChange(bizType)}>
              {bizType === PHONE_BIZTYPE ? t(SIGN_EMAIL_TAB_KEY) : t(SIGN_PHONE_TAB_KEY)}
            </TabBox>
          </TopBox>

          <Tips mb={3}>
            {t('signup_num_pre')}
            {NumberComponent}
            {t('signup_num_sur')}
          </Tips>

          <Box>
            <Box
              style={{
                display: bizType !== PHONE_BIZTYPE ? 'none' : 'block',
              }}
            >
              <PhoneSign
                defaultCountryCode={defaultCountryCode}
                agreeJSX={agreeJSX}
                onChange={onChange}
                isKumex
                trackingConfig={trackingConfig}
                fromDrawer
              />
            </Box>
            <Box
              style={{
                display: bizType !== EMAIL_BIZTYPE ? 'none' : 'block',
              }}
            >
              <EmailSign
                agreeJSX={agreeJSX}
                onChange={onChange}
                isKumex
                trackingConfig={trackingConfig}
                fromDrawer
              />
            </Box>
          </Box>
          <Box mt={3} mb={3}>
            {t('signup_change_login')}
            <TabBox as="span" onClick={handleLogin}>
              {t('login')}
            </TabBox>
            <Box>{ZendeskIcon}</Box>
          </Box>
          <Divider />

          <Box>{Footer}</Box>

          <HumanCaptcha bizType={bizType} namespace={NAMESPACE} nowPage="signUp" />
        </Box>
      </>
    </Drawer>
  );
};

export const Signup4KuMEXDrawer = ({ theme, ...others }) => {
  return (
    <ThemeProvider theme={theme || 'light'}>
      <Signup4KuMEXDrawerWithTheme {...others} />
    </ThemeProvider>
  );
};

const SignUpDrawerWithTheme = (props = {}) => {
  const {
    onClose,
    onChange,
    agreeJSX,
    BoxProps = {},
    tabKey,
    trackingConfig = {},
    ...resetProps
  } = props;

  const theme = useTheme();

  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch({
      type: `${NAMESPACE}/reset`,
    });
    if (onClose) {
      onClose();
    }
  };

  // 侧边栏注册-曝光埋点
  useEffect(() => {
    if (resetProps.open) {
      kcsensorsManualTrack({
        kc_pageid: 'B1sideRegister',
        spm: ['popUp', '1'],
        ...trackingConfig,
      });
    }
  }, [resetProps.open]);

  return (
    <Drawer {...resetProps}>
      <CloseBox onClick={handleClose}>
        <CloseIcon theme={theme}>
          <CloseOutlined size={24} />
        </CloseIcon>
      </CloseBox>
      <SignUp
        tabKey={tabKey}
        onChange={onChange}
        agreeJSX={agreeJSX}
        BoxProps={{ width: 528, p: 8, ...BoxProps }}
        trackingConfig={trackingConfig}
        fromDrawer
      />
    </Drawer>
  );
};

export const SignUpDrawer = ({ theme, ...others }) => {
  return (
    <ThemeProvider theme={theme}>
      <SignUpDrawerWithTheme {...others} />
    </ThemeProvider>
  );
};

export const SignUpNoLayout = (props) => {
  return <SignUpWithTheme {...props} />;
};

export default withLayout(SignUpWithTheme);
